/**
 * Ticket Lifecycle v2 — FOH Handoff Reason Picker (Batch 1)
 *
 * Small, reusable dialog used by the manual kebab Kick-to-Advisor /
 * Kick-Back-to-Tech flows and (in a later batch) the Pause / Stop
 * dialogs. The dialog itself is trigger-agnostic — callers pick the
 * reason set via the `trigger` + `eventKind` props.
 *
 * The selected reason is stored verbatim in
 * `ticket_custody_events.reason`. The metadata payload (built by
 * `buildCustodyHandoffMetadata`) is the caller's responsibility — this
 * component returns just the reason value + the picked label so the
 * caller has everything they need to construct the payload.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  getCustodyHandoffReasonsFor,
  type CustodyHandoffTrigger,
  type CustodyHandoffReasonOption,
} from '@/lib/tickets/lifecycle/custodyHandoffReasons';

export interface CustodyHandoffReasonDialogResult {
  reason: string;
  reasonLabel: string;
  notes: string | null;
}

export interface CustodyHandoffReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Determines which canonical reason set is shown. */
  trigger: CustodyHandoffTrigger;
  /**
   * The custody event being initiated. Drives the dialog title and
   * forces the kick-back reason set when `kick_to_boh`.
   */
  eventKind: 'kick_to_foh' | 'kick_to_boh' | 'reassign_within_foh' | string;
  /** Optional title override. */
  title?: string;
  /** Optional description override. */
  description?: string;
  /** Confirm-button label. Defaults match the picked event kind. */
  confirmLabel?: string;
  /** Disable while parent mutation is pending. */
  isPending?: boolean;
  onConfirm: (result: CustodyHandoffReasonDialogResult) => void;
  onCancel?: () => void;
}

function defaultTitle(
  eventKind: CustodyHandoffReasonDialogProps['eventKind'],
): string {
  if (eventKind === 'kick_to_foh') return 'Kick to advisor — pick a reason';
  if (eventKind === 'kick_to_boh') return 'Kick back to tech — pick a reason';
  return 'Custody handoff — pick a reason';
}

function defaultDescription(
  eventKind: CustodyHandoffReasonDialogProps['eventKind'],
): string {
  if (eventKind === 'kick_to_foh')
    return 'Choose the closest reason. This drives FOH analytics — please avoid "Other" when possible.';
  if (eventKind === 'kick_to_boh')
    return 'Choose why the ticket is being returned to the technician.';
  return 'Choose a reason for this custody handoff.';
}

function defaultConfirmLabel(
  eventKind: CustodyHandoffReasonDialogProps['eventKind'],
): string {
  if (eventKind === 'kick_to_foh') return 'Kick to Advisor';
  if (eventKind === 'kick_to_boh') return 'Kick Back to Tech';
  return 'Confirm';
}

export function CustodyHandoffReasonDialog({
  open,
  onOpenChange,
  trigger,
  eventKind,
  title,
  description,
  confirmLabel,
  isPending = false,
  onConfirm,
  onCancel,
}: CustodyHandoffReasonDialogProps) {
  const options: ReadonlyArray<CustodyHandoffReasonOption> =
    getCustodyHandoffReasonsFor(trigger, eventKind);
  const firstValue = options[0]?.value ?? 'other';
  const [selected, setSelected] = useState<string>(firstValue);
  const [notes, setNotes] = useState<string>('');

  // Reset selection each time the dialog opens so a stale picker from
  // a prior ticket doesn't pre-fill the wrong reason.
  useEffect(() => {
    if (open) {
      setSelected(firstValue);
      setNotes('');
    }
  }, [open, firstValue]);

  const handleConfirm = () => {
    const opt = options.find((o) => o.value === selected);
    onConfirm({
      reason: selected,
      reasonLabel: opt?.label ?? selected,
      notes: notes.trim() ? notes.trim() : null,
    });
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="custody-handoff-reason-dialog">
        <DialogHeader>
          <DialogTitle>{title ?? defaultTitle(eventKind)}</DialogTitle>
          <DialogDescription>
            {description ?? defaultDescription(eventKind)}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 max-h-[60vh] overflow-y-auto">
          <RadioGroup
            value={selected}
            onValueChange={setSelected}
            className="space-y-2"
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                className="flex items-start gap-3 rounded-md border p-3 hover:bg-accent/40 cursor-pointer"
                onClick={() => setSelected(opt.value)}
                data-testid={`custody-reason-${opt.value}`}
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`custody-reason-radio-${opt.value}`}
                  className="mt-1"
                />
                <Label
                  htmlFor={`custody-reason-radio-${opt.value}`}
                  className="flex-1 cursor-pointer space-y-0.5"
                >
                  <div className="font-medium">{opt.label}</div>
                  {opt.description && (
                    <div className="text-xs text-muted-foreground">
                      {opt.description}
                    </div>
                  )}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="mt-3 space-y-1">
            <Label htmlFor="custody-reason-notes" className="text-xs">
              Notes (optional)
            </Label>
            <Textarea
              id="custody-reason-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything FOH should know."
              data-testid="custody-reason-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending || !selected}
            data-testid="custody-reason-confirm"
          >
            {isPending ? 'Saving…' : (confirmLabel ?? defaultConfirmLabel(eventKind))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
